import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { TransactionDetailsEntity, TransactionLineEntity } from "@modules/finances/entities/transactionDetailsEntity"
import { DiscountType } from "@modules/finances/gateway/transactionDto"
import { TransactionStatus } from "@modules/finances/types/financesType"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { DataTable } from "@modules/ui/components/dataTables/datatable"
import { shortenText } from "@modules/utils/stringUtils"
import dayjs from "dayjs"
import { Column } from "primereact/column"
import { ColumnGroup } from "primereact/columngroup"
import { Row } from "primereact/row"
import styled from "styled-components"

type InvoiceDetailsTableProps = { transactionDetails: TransactionDetailsEntity }

export const InvoiceDetailsTable: React.FC<InvoiceDetailsTableProps> = ({ transactionDetails }) => {
  const language = useLanguage()

  const hasDiscount = transactionDetails.discounts.length > 0
  const discountsPromoCode = transactionDetails.discounts.filter(
    (discount) => discount.type === DiscountType.PROMO_CODE,
  )
  const discountsCoupon = transactionDetails.discounts.filter((discount) => discount.type === DiscountType.DISCOUNT)
  const totalAmount = transactionDetails.lines.reduce((acc, line) => acc + line.totalAmount, 0)

  let footerGroup = <ColumnGroup></ColumnGroup>
  if (transactionDetails.refunded) {
    footerGroup = (
      <ColumnGroup>
        {discountsPromoCode.length > 0 &&
          discountsPromoCode.map((promoCode, promoCodeIndex) => (
            <Row key={promoCodeIndex}>
              <Column
                className="font-normal p-1 pt-4 text-right pr-4"
                footer={promoCode.name ? word("promoCode.short", { code: shortenText(promoCode.name, 10) }) : ""}
                colSpan={3}
              />
              <Column
                className="font-normal p-1 pt-4 text-right"
                footer={formatCurrency(
                  promoCode.couponType === CouponType.DIRECT
                    ? -promoCode.amount
                    : -(totalAmount * promoCode.amount) / 100,
                  transactionDetails.currency,
                  language,
                )}
              />
            </Row>
          ))}
        {discountsCoupon.length > 0 &&
          discountsCoupon.map((discountCoupon, discountCouponIndex) => (
            <Row key={discountCouponIndex}>
              <Column
                className="font-normal p-1 pt-4 text-right pr-4"
                footer={word("box.member.table.offer.item.discount.label")}
                colSpan={3}
              />
              <Column
                className="font-normal p-1 pt-4 text-right"
                footer={formatCurrency(
                  discountCoupon.couponType === CouponType.DIRECT
                    ? -discountCoupon.amount
                    : discountCoupon.amount < 100
                      ? -(discountCoupon.amount * transactionDetails.amount) / (100 - discountCoupon.amount)
                      : -transactionDetails.amount,
                  transactionDetails.currency,
                  language,
                )}
              />
            </Row>
          ))}
        <Row>
          <Column
            className={"font-normal p-1 text-right pr-4" + (hasDiscount ? " border-top-none pt-2" : " pt-4")}
            footer={word("finances.array.invoice.excludingVATTotal")}
            colSpan={3}
          />
          <Column
            className={"font-normal p-1 text-right" + (hasDiscount ? " border-top-none pt-2" : " pt-4")}
            footer={formatCurrency(
              (transactionDetails.amount || 0) - (transactionDetails.totalTaxAmount || 0),
              transactionDetails.currency,
              language,
            )}
          />
        </Row>
        <Row>
          <Column
            className="border-top-none font-normal p-1 pt-2 text-right pr-4"
            footer={word("finances.array.invoice.VATTotal")}
            colSpan={3}
          />
          <Column
            className="border-top-none font-normal p-1 pt-2 text-right"
            footer={formatCurrency(transactionDetails.totalTaxAmount || 0, transactionDetails.currency, language)}
          />
        </Row>

        <Row>
          <Column
            className="border-top-none p-1 pt-2 text-right pr-4"
            footer={word("finances.array.invoice.refund")}
            colSpan={3}
          />
          <Column
            className="border-top-none p-1 pt-2 text-right"
            footer={formatCurrency(-transactionDetails.amount || 0, transactionDetails.currency, language)}
          />
        </Row>
        <Row>
          <Column
            className="border-top-none p-1 pt-2 text-right pr-4"
            footer={word("finances.array.invoice.fee")}
            colSpan={3}
          />
          <Column
            className="border-top-none p-1 pt-2 text-right"
            footer={formatCurrency(transactionDetails.fee || 0, transactionDetails.currency, language)}
          />
        </Row>
        <Row>
          <Column
            className="border-top-none p-1 pt-2 text-right pr-4"
            footer={word("finances.array.invoice.includingVATTotal")}
            colSpan={3}
          />
          <Column
            className="border-top-none p-1 pt-2 text-right"
            footer={formatCurrency(-transactionDetails.fee || 0, transactionDetails.currency, language)}
          />
        </Row>
      </ColumnGroup>
    )
  } else if (transactionDetails.disputed && !!transactionDetails.disputeDetail) {
    footerGroup = (
      <ColumnGroup>
        <Row>
          <Column
            className={"font-normal p-1 text-right pr-4" + (hasDiscount ? " border-top-none pt-2" : " pt-4")}
            footer={word("finances.array.invoice.dispute.paymentAmount")}
            colSpan={3}
          />
          <Column
            className={"font-normal p-1 text-right" + (hasDiscount ? " border-top-none pt-2" : " pt-4")}
            footer={formatCurrency(transactionDetails.amount || 0, transactionDetails.currency, language)}
          />
        </Row>
        <Row>
          <Column
            className="border-top-none font-normal p-1 pt-2 text-right pr-4"
            footer={word("finances.array.invoice.dispute.disputedAmount")}
            colSpan={3}
          />
          <Column
            className="border-top-none font-normal p-1 pt-2 text-right"
            footer={
              "- " + formatCurrency(transactionDetails.disputeDetail.amount, transactionDetails.currency, language)
            }
          />
        </Row>
        <Row>
          <Column
            className="border-top-none font-normal p-1 pt-2 text-right pr-4"
            footer={word("finances.array.invoice.dispute.fee")}
            colSpan={3}
          />
          <Column
            className="border-top-none font-normal p-1 pt-2 text-right"
            footer={"- " + formatCurrency(transactionDetails.fee, transactionDetails.currency, language)}
          />
        </Row>
        <Row>
          <Column
            className="border-top-none font-normal p-1 pt-2 text-right pr-4"
            footer={word("finances.array.invoice.dispute.disputeFee")}
            colSpan={3}
          />
          <Column
            className="border-top-none font-normal p-1 pt-2 text-right"
            footer={"- " + formatCurrency(transactionDetails.disputeDetail.fee, transactionDetails.currency, language)}
          />
        </Row>
        <Row>
          <Column
            className="border-top-none p-1 pt-2 text-right pr-4"
            footer={word("finances.array.invoice.dispute.total")}
            colSpan={3}
          />
          <Column
            className="border-top-none p-1 pt-2 text-right"
            footer={
              "- " +
              formatCurrency(
                transactionDetails.fee + transactionDetails.disputeDetail.fee,
                transactionDetails.currency,
                language,
              )
            }
          />
        </Row>
      </ColumnGroup>
    )
  } else {
    footerGroup = (
      <ColumnGroup>
        {discountsPromoCode.length > 0 &&
          discountsPromoCode.map((promoCode, promoCodeIndex) => (
            <Row key={promoCodeIndex}>
              <Column
                className="font-normal p-1 pt-4 text-right pr-4"
                footer={promoCode.name ? word("promoCode.short", { code: shortenText(promoCode.name, 10) }) : ""}
                colSpan={3}
              />
              <Column
                className="font-normal p-1 pt-4 text-right"
                footer={formatCurrency(
                  promoCode.couponType === CouponType.DIRECT
                    ? -promoCode.amount
                    : -(totalAmount * promoCode.amount) / 100,
                  transactionDetails.currency,
                  language,
                )}
              />
            </Row>
          ))}
        {discountsCoupon.length > 0 &&
          discountsCoupon.map((discountCoupon, discountCouponIndex) => (
            <Row key={discountCouponIndex}>
              <Column
                className="font-normal p-1 pt-4 text-right pr-4"
                footer={word("box.member.table.offer.item.discount.label")}
                colSpan={3}
              />
              <Column
                className="font-normal p-1 pt-4 text-right"
                footer={formatCurrency(
                  discountCoupon.couponType === CouponType.DIRECT
                    ? -discountCoupon.amount
                    : discountCoupon.amount < 100
                      ? -(discountCoupon.amount * transactionDetails.amount) / (100 - discountCoupon.amount)
                      : -transactionDetails.amount,
                  transactionDetails.currency,
                  language,
                )}
              />
            </Row>
          ))}
        <Row>
          <Column
            className={"font-normal p-1 text-right pr-4" + (hasDiscount ? " border-top-none pt-2" : " pt-4")}
            footer={word("finances.array.invoice.excludingVATTotal")}
            colSpan={3}
          />
          <Column
            className={"font-normal p-1 text-right" + (hasDiscount ? " border-top-none pt-2" : " pt-4")}
            footer={formatCurrency(
              (transactionDetails.amount || 0) - (transactionDetails.totalTaxAmount || 0),
              transactionDetails.currency,
              language,
            )}
          />
        </Row>
        <Row>
          <Column
            className="border-top-none font-normal p-1 pt-2 text-right pr-4"
            footer={word("finances.array.invoice.VATTotal")}
            colSpan={3}
          />
          <Column
            className="border-top-none font-normal p-1 pt-2 text-right"
            footer={formatCurrency(transactionDetails.totalTaxAmount || 0, transactionDetails.currency, language)}
          />
        </Row>
        <Row>
          <Column
            className="border-top-none font-normal p-1 pt-2 text-right pr-4"
            footer={word("finances.array.invoice.fees")}
            colSpan={3}
          />
          <Column
            className="border-top-none font-normal p-1 pt-2 text-right"
            footer={
              transactionDetails.fee !== 0
                ? "- " + formatCurrency(transactionDetails.fee || 0, transactionDetails.currency, language)
                : "--"
            }
          />
        </Row>
        <Row>
          <Column
            className="border-top-none p-1 pt-2 text-right pr-4"
            footer={
              transactionDetails.status === TransactionStatus.PENDING
                ? word("finances.array.invoice.dueAmount")
                : word("finances.array.invoice.includingVATTotal")
            }
            colSpan={3}
          />
          <Column
            className="border-top-none p-1 pt-2 text-right"
            footer={formatCurrency(transactionDetails.netAmount || 0, transactionDetails.currency, language)}
          />
        </Row>
      </ColumnGroup>
    )
  }

  return (
    <>
      <StyledInvoiceDetailsTable
        value={transactionDetails.lines as TransactionLineEntity[]}
        dataKey="description"
        emptyMessage={word("global.no_result")}
        footerColumnGroup={footerGroup}
      >
        <Column
          header={word("box.form.description.label")}
          body={(data) => {
            const periodStart = data.periodStart ? dayjs(data.periodStart).format("L") : ""
            const commat = periodStart && data.periodEnd ? " - " : ""
            const periodEnd = data.periodEnd ? dayjs(data.periodEnd).format("L") : ""
            return `${data.description} (${periodStart}${commat}${periodEnd})`
          }}
        />

        <Column
          header={word("finances.array.invoice.net_price")}
          body={(data) => formatCurrency(data.netAmount, data.currency, language)}
        />
        <Column
          header={word("finances.array.invoice.taxe_price_percent")}
          field="taxPercent"
          body={(data) => data.taxPercent}
        />
        <Column
          header={word("finances.array.invoice.total")}
          body={(data) => formatCurrency(data.totalAmount, data.currency, language)}
        />
      </StyledInvoiceDetailsTable>
    </>
  )
}

const StyledInvoiceDetailsTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background-color: transparent;
    color: ${(props) => props.theme.textPrimary};
    font-size: 1rem;
    font-weight: 600;
    white-space: nowrap;
    padding: 10px 5px;
    border-bottom: 1px solid ${(props) => props.theme.neutralColor300};
  }

  .p-datatable-tbody > tr {
    font-size: 0.8rem !important;
    font-weight: 400 !important;
    line-height: 15.34px;
    color: ${(props) => props.theme.neutralColor600};
  }
  .p-datatable-tbody > tr > td {
    padding: 10px 5px !important;
  }
  .p-datatable-tfoot > tr > td {
    padding: 10px 5px;
    background-color: transparent;
    color: ${(props) => props.theme.textPrimary};
    font-size: 1rem;
    font-weight: 600;
    white-space: nowrap;
    border-top: 1px solid ${(props) => props.theme.neutralColor300};
    border-bottom: none;
  }
`
